import axios from "axios";

// Provider token exchange.
//
// Trades the passwordless Cognito idToken (sessionStorage 'token') for a provider-scoped
// practitioner admin token, persists it, and returns it together with the provider's care
// teams. This is the single source of truth for the exchange — both the provider-selection
// grid and the FHIR-admin console entry point call it (the console passes the reserved
// "dearhealth" provider id). Behaviour is intentionally identical to the original inline
// implementation that lived in pages/providers.tsx.

export const FHIR_ADMIN_PROVIDER_ID = "dearhealth";

export interface ProviderTokenResult {
  adminToken: string;
  careTeams: any[];
}

// The region is inferred from the host TLD (".com" => US, otherwise EU), matching the
// original provider-selection logic.
function tokenApiBaseUrl(): string | undefined {
  const isUsHost = window.location.host.split(".").pop() === "com";
  return isUsHost
    ? process.env.REACT_APP_practitionerAuthTokenApi_US
    : process.env.REACT_APP_practitionerAuthTokenApi_EU;
}

export async function exchangeProviderToken(
  providerId: string
): Promise<ProviderTokenResult> {
  // The frontend knows which factor established this Cognito session; pass it so the admin JWT records
  // loginMethod=passkey (audit hint). Defaults to passwordless for the email/SMS OTP flow.
  const loginMethod = sessionStorage.getItem("loginMethod") || "passwordless";
  const response = await axios.get(
    `${tokenApiBaseUrl()}${providerId}&role=admin&loginMethod=${encodeURIComponent(loginMethod)}`,
    {
      headers: {
        Authorization: sessionStorage.getItem("token")!,
      },
    }
  );

  const adminToken = response.data["pracAuthToken"];
  sessionStorage.setItem("adminToken", adminToken);
  sessionStorage.setItem("providerId", providerId);

  // Care teams power the Overview/push-content screens; a failure here must not block the
  // token exchange (e.g. the "dearhealth" console provider has no medical-content record).
  let careTeams: any[] = [];
  try {
    const providerResponse = await axios.get(
      `${process.env.REACT_APP_MEDICAL_CONTENT_URL}/providers/${providerId}`,
      { headers: { authorization: adminToken } }
    );
    careTeams = providerResponse.data.careTeams || [];
  } catch (error) {
    console.error("Failed to fetch care teams:", error);
  }

  return { adminToken, careTeams };
}
